'use client';

import { type ChangeEvent, useCallback, useEffect, useMemo } from 'react';

import { type FileInputErrorDetails, type TextAreaState } from '@videoblocks/design-system';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import {
    validateImageGenerationForm,
    IMAGE_GENERATION_FORM_DEFAULTS,
    type ImageGenerationFormErrors,
    type ImageGenerationFormState,
} from '../../_shared/_context/ImageGenerationFormState';
import {
    getImageGenerationCreditCost,
    IMAGE_GENERATION_RATIO_MAP,
    MAX_PROMPT_LENGTH,
} from '../../_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES, type AiTaskSubmitRouteResponse } from '../../_shared/_context/types';
import { fileToDataUrl } from '../../_shared/_utils/fileToDataUrl';

const IMAGE_GENERATION_ENDPOINT = '/api/image-generation';

export type UseImageGenerationFormReturn = {
    formState: ImageGenerationFormState;
    errors: ImageGenerationFormErrors;
    promptState: TextAreaState;
    promptHelperText: string | undefined;
    isValid: boolean;
    isSubmitDisabled: boolean;
    calculatedCost: number;
    onPromptChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onPromptBlur: () => void;
    onAspectRatioChange: (value: string) => void;
    onResolutionChange: (value: string) => void;
    onReferenceImagesSelect: (files: File | File[]) => void;
    onReferenceImageRemove: (index: number) => void;
    onFileError: (details: FileInputErrorDetails) => string;
    onSubmit: () => void;
    onConfirm: () => Promise<void>;
};

export const useImageGenerationForm = (): UseImageGenerationFormReturn => {
    const {
        formStates,
        updateFormState,
        dispatchImageGenerationSubmit,
        dispatchConfirmationModal,
        submitStates,
        refreshCredits,
        setImageGenerationSubmitError,
    } = useAiToolkitContext();
    const imageSubmitState = submitStates[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION];
    const isInProgress = imageSubmitState.status === 'inProgress';

    const formState = formStates[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION] ?? IMAGE_GENERATION_FORM_DEFAULTS;
    const validationActive = formState.validationActive;

    useEffect(() => {
        if (!formStates[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]) {
            updateFormState(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, IMAGE_GENERATION_FORM_DEFAULTS);
        }
    }, [formStates, updateFormState]);

    const { isValid, errors: schemaErrors } = useMemo(() => validateImageGenerationForm(formState), [formState]);

    const isSubmitDisabled = validationActive && !isValid;

    const errors: ImageGenerationFormErrors = useMemo(() => {
        if (!validationActive) {
            return {};
        }
        return { ...schemaErrors };
    }, [validationActive, schemaErrors]);

    const promptState: TextAreaState = useMemo(() => {
        if (validationActive && schemaErrors.prompt) {
            return 'error';
        }
        return 'default';
    }, [validationActive, schemaErrors.prompt]);

    const calculatedCost = useMemo(() => getImageGenerationCreditCost(formState.resolution), [formState.resolution]);

    const promptHelperText = useMemo((): string | undefined => {
        if (validationActive && schemaErrors.prompt) {
            return schemaErrors.prompt;
        }
        if (formState.prompt.length >= MAX_PROMPT_LENGTH) {
            return `You've reached the maximum allowed length (${MAX_PROMPT_LENGTH.toLocaleString()} characters).`;
        }
        return undefined;
    }, [validationActive, schemaErrors.prompt, formState.prompt.length]);

    const updateField = useCallback(
        <K extends keyof ImageGenerationFormState>(field: K, value: ImageGenerationFormState[K]): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, { ...formState, [field]: value });
        },
        [formState, updateFormState],
    );

    const onPromptChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>): void => {
            updateField('prompt', e.target.value);
        },
        [updateField],
    );

    const onPromptBlur = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, { ...formState, validationActive: true });
    }, [formState, updateFormState]);

    const onAspectRatioChange = useCallback(
        (value: string): void => {
            updateField('aspectRatio', value);
        },
        [updateField],
    );

    const onResolutionChange = useCallback(
        (value: string): void => {
            updateField('resolution', value);
        },
        [updateField],
    );

    const onReferenceImagesSelect = useCallback(
        (files: File | File[]): void => {
            const newFiles = Array.isArray(files) ? files : [files];
            updateFormState(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, {
                ...formState,
                referenceImages: [...formState.referenceImages, ...newFiles],
            });
        },
        [formState, updateFormState],
    );

    const onReferenceImageRemove = useCallback(
        (index: number): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, {
                ...formState,
                referenceImages: formState.referenceImages.filter((_, i) => i !== index),
            });
        },
        [formState, updateFormState],
    );

    const onFileError = useCallback((details: FileInputErrorDetails): string => {
        switch (details.type) {
            case 'unsupported_type':
                return "This file format isn't supported. Try JPEG, PNG, or WebP.";
            case 'file_too_large':
                return 'File exceeds 15MB limit. Please choose a smaller image.';
            case 'too_many_files':
                return "You've reached the 3 image limit.";
            case 'processing_error':
                return 'Upload failed. Please try again.';
        }
    }, []);

    const onSubmit = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, { ...formState, validationActive: true });

        if (!isValid) {
            return;
        }

        dispatchConfirmationModal({
            type: 'OPEN_MODAL',
            modal: { type: 'creditConfirmation', category: AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION },
        });
    }, [formState, updateFormState, isValid, dispatchConfirmationModal]);

    const onConfirm = useCallback(async (): Promise<void> => {
        if (isInProgress) return;
        dispatchImageGenerationSubmit({ type: 'GENERATION_IN_PROGRESS' });
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
        setImageGenerationSubmitError(null);

        // Capture the content type and model at submit time so later form edits (e.g. removing
        // reference images) cannot misreport the generation on the download/feedback events.
        const usesReferenceImages = formState.referenceImages.length > 0;
        const aiContentType = usesReferenceImages ? AiContentType.IMAGE_TO_IMAGE : AiContentType.TEXT_TO_IMAGE;
        const aiModelName = formState.model;

        try {
            const ratio = IMAGE_GENERATION_RATIO_MAP[formState.aspectRatio]?.[formState.resolution] ?? '1280:720';
            const imageUrls =
                formState.referenceImages.length > 0
                    ? await Promise.all(formState.referenceImages.map(fileToDataUrl))
                    : undefined;

            const body = {
                prompt: formState.prompt.trim(),
                imageUrls,
                algoParams: { modelId: aiModelName, ratio },
            };

            const response = await fetch(IMAGE_GENERATION_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            void refreshCredits();

            const payload = (await response.json()) as AiTaskSubmitRouteResponse;

            if (!response.ok || payload.success !== true) {
                const message =
                    'message' in payload && payload.message
                        ? payload.message
                        : 'error' in payload && payload.error
                          ? payload.error
                          : 'Image generation failed. Please try again.';
                const failureReason = 'failureReason' in payload ? payload.failureReason ?? null : null;
                setImageGenerationSubmitError(message);
                dispatchImageGenerationSubmit({ type: 'GENERATION_ERROR', failureReason });
                return;
            }

            dispatchImageGenerationSubmit({
                type: 'GENERATION_IN_PROGRESS',
                pendingTaskId: payload.taskId,
                aiContentId: payload.aiContentId,
                lastAiContentId: payload.lastAiContentId,
                aiContentType,
                aiModelName,
            });
        } catch (err) {
            void refreshCredits();
            setImageGenerationSubmitError(
                err instanceof Error ? err.message : 'Image generation failed. Please try again.',
            );
            dispatchImageGenerationSubmit({ type: 'GENERATION_ERROR' });
        }
    }, [
        dispatchConfirmationModal,
        dispatchImageGenerationSubmit,
        formState,
        isInProgress,
        refreshCredits,
        setImageGenerationSubmitError,
    ]);

    return {
        formState,
        errors,
        promptState,
        promptHelperText,
        isValid,
        isSubmitDisabled,
        calculatedCost,
        onPromptChange,
        onPromptBlur,
        onAspectRatioChange,
        onResolutionChange,
        onReferenceImagesSelect,
        onReferenceImageRemove,
        onFileError,
        onSubmit,
        onConfirm,
    };
};
